import api from './api';

// TomTom API Types
export interface TomTomGeocodeResult {
  latitude: number;
  longitude: number;
  formattedAddress: string;
  city?: string;
  country?: string;
  score?: number;
}

export interface TomTomWaypoint {
  latitude: number;
  longitude: number;
  name?: string;
  address?: string;
}

export interface TomTomRoutePoint {
  latitude: number;
  longitude: number;
}

export interface TomTomInstruction {
  pointIndex: number;
  maneuver: string;
  message: string;
  roadName?: string;
  distanceMeters?: number;
  travelTimeSeconds?: number;
  latitude: number;
  longitude: number;
}

export interface TomTomRouteSummary {
  totalDistanceKm: number;
  totalTimeMinutes: number;
  departureTime?: string;
  arrivalTime?: string;
  trafficDelaySeconds: number;
}

export interface TomTomRouteResult {
  distanceMeters: number;
  travelTimeSeconds: number;
  points: TomTomRoutePoint[];
  instructions: TomTomInstruction[];
  summary: TomTomRouteSummary;
}

export interface SegmentRouteInfo {
  segmentId: string;
  location: string;
  sequenceOrder: number;
  coordinates?: TomTomGeocodeResult;
  distanceToNextKm?: number;
  timeToNextMinutes?: number;
  vehicleName?: string;
  driverName?: string;
  partnerName?: string;
  /** Vehicle type of this segment (numeric enum value, see VehicleType). */
  vehicleType?: number;
  /** Localized (German) display name of the vehicle type. */
  vehicleTypeName?: string;
  /**
   * Geometry of the leg from this segment to the next one.
   * Road legs contain the TomTom route points; air/sea legs are undefined
   * and are rendered as a straight line by the frontend.
   */
  legPoints?: TomTomRoutePoint[];
}

export interface MapMarkerWithPixel {
  lat: number;
  lon: number;
  label?: string;
  pixel: { x: number; y: number };
}

export interface MapInfo {
  center: { lat: number; lon: number };
  zoom: number;
  width: number;
  height: number;
}

export interface PlanningRouteResult {
  planningId: string;
  planningName: string;
  segments: SegmentRouteInfo[];
  fullRoute?: TomTomRouteResult;
  totalDistanceKm: number;
  totalTimeMinutes: number;
  mapImageBase64?: string;
  mapMarkers?: MapMarkerWithPixel[];
  mapInfo?: MapInfo;
}

export interface TomTomMapRequest {
  markers: TomTomWaypoint[];
  routePoints?: TomTomRoutePoint[];
  width?: number;
  height?: number;
  zoom?: number;
}

export interface MapImageResponse {
  imageBase64: string;
}

// TomTom API Service
export const tomtomApi = {
  /**
   * Geocode an address to coordinates
   */
  geocode: async (address: string): Promise<TomTomGeocodeResult> => {
    const response = await api.get<TomTomGeocodeResult>('/api/tomtom/geocode', {
      params: { address }
    });
    return response.data;
  },

  /**
   * Calculate route between waypoints
   */
  calculateRoute: async (waypoints: TomTomWaypoint[]): Promise<TomTomRouteResult> => {
    const response = await api.post<TomTomRouteResult>('/api/tomtom/route', {
      waypoints
    });
    return response.data;
  },

  /**
   * Calculate route for a planning (all segments)
   */
  calculatePlanningRoute: async (planningId: number): Promise<PlanningRouteResult> => {
    const response = await api.get<PlanningRouteResult>(`/api/tomtom/planning/${planningId}/route`);
    return response.data;
  },

  /**
   * Get static map image
   */
  getStaticMap: async (request: TomTomMapRequest): Promise<MapImageResponse> => {
    const response = await api.post<MapImageResponse>('/api/tomtom/map', request);
    return response.data;
  },

  /**
   * Format distance for display
   */
  formatDistance: (km: number): string => {
    if (km < 1) {
      return `${Math.round(km * 1000)} m`;
    }
    return `${km.toFixed(1)} km`;
  },

  /**
   * Format time for display
   */
  formatTime: (minutes: number): string => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  }
};

export default tomtomApi;

